import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { I18nProvider } from './hooks/useI18n';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import ChatBot from './components/ChatBot';
import CookieBanner from './components/CookieBanner';
import ExternalLinkWarning from './components/ExternalLinkWarning';

// Public Pages - Core (eager loaded)
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import VarietiesPagePublic from './pages/VarietiesPage';
import TechnologiesPagePublic from './pages/TechnologiesPage';
import ResearchPagePublic from './pages/ResearchPage';
import PublicationsPage from './pages/PublicationsPage';
import FarmersPage from './pages/FarmersPage';
import NurseryPage from './pages/NurseryPage';
import MediaPage from './pages/MediaPage';

// Public Pages - Internal (lazy loaded)
const StaffPage = lazy(() => import('./pages/StaffPage'));
const DivisionPage = lazy(() => import('./pages/DivisionPage'));
const CadreStrengthPage = lazy(() => import('./pages/CadreStrengthPage'));
const VisionPage = lazy(() => import('./pages/VisionPage'));
const LibraryPage = lazy(() => import('./pages/LibraryPage'));
const MuseumPage = lazy(() => import('./pages/MuseumPage'));
const AuditoriumPage = lazy(() => import('./pages/AuditoriumPage'));
const GuestHousePage = lazy(() => import('./pages/GuestHousePage'));
const PhotosPage = lazy(() => import('./pages/PhotosPage'));
const FieldGeneBankPage = lazy(() => import('./pages/FieldGeneBankPage'));
const JobsPagePublic = lazy(() => import('./pages/JobsPagePublic'));
const TendersPagePublic = lazy(() => import('./pages/TendersPagePublic'));
const AnnouncementsPagePublic = lazy(() => import('./pages/AnnouncementsPage'));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'));
const AnnualReportPage = lazy(() => import('./pages/AnnualReportPage'));
const EPublicationsPage = lazy(() => import('./pages/EPublicationsPage'));
const CitizenCharterPage = lazy(() => import('./pages/CitizenCharterPage'));
const ITMUPage = lazy(() => import('./pages/ITMUPage'));
const ABIPage = lazy(() => import('./pages/ABIPage'));
const CollaborationPage = lazy(() => import('./pages/CollaborationPage'));
const SuccessStoriesPage = lazy(() => import('./pages/SuccessStoriesPage'));
const { SuccessStoryDetailPage } = require('./pages/SuccessStoriesPage');
const KVKMaldaPage = lazy(() => import('./pages/KVKMaldaPage'));
const RRSMaldaPage = lazy(() => import('./pages/RRSMaldaPage'));
const ConsultancyPage = lazy(() => import('./pages/ConsultancyPage'));
const CommercializationPage = lazy(() => import('./pages/CommercializationPage'));
const IPRProtectionPage = lazy(() => import('./pages/IPRProtectionPage'));
const ClimateResilientPage = lazy(() => import('./pages/ClimateResilientPage'));
// New pages
const AgroAdvisoryPage = lazy(() => import('./pages/AgroAdvisoryPage'));
const ContractResearchPage = lazy(() => import('./pages/ContractResearchPage'));
const ContractServicesPage = lazy(() => import('./pages/ContractServicesPage'));
const CISHPublicationsPage = lazy(() => import('./pages/CISHPublicationsPage'));
const EducationPage = lazy(() => import('./pages/EducationPage'));
const NutraceuticalPage = lazy(() => import('./pages/NutraceuticalPage'));
const ResearchFundedPage = lazy(() => import('./pages/ResearchFundedPage'));
const ServiceFunctionsPage = lazy(() => import('./pages/ServiceFunctionsPage'));
const TrainingAttendedPage = lazy(() => import('./pages/TrainingAttendedPage'));
const VKSAPage = lazy(() => import('./pages/VKSAPage'));
// Additional pages from updated menu
const PMEPage = lazy(() => import('./pages/PMEPage'));
const AKMUPage = lazy(() => import('./pages/AKMUPage'));
const UdyanRashmiPage = lazy(() => import('./pages/UdyanRashmiPage'));
const PlantBookingsPage = lazy(() => import('./pages/PlantBookingsPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage'));

// Admin Pages
import LoginPage from './pages/admin/LoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import ApprovalQueuePage from './pages/admin/ApprovalQueuePage';
import BannersPage from './pages/admin/BannersPage';
import AnnouncementsPage from './pages/admin/AnnouncementsPage';
import DirectorPage from './pages/admin/DirectorPage';
import VarietiesPage from './pages/admin/VarietiesPage';
import TechnologiesPage from './pages/admin/TechnologiesPage';
import ResearchPage from './pages/admin/ResearchPage';
import JobsPage from './pages/admin/JobsPage';
import TendersPage from './pages/admin/TendersPage';
import NewsPage from './pages/admin/NewsPage';
import LinksPage from './pages/admin/LinksPage';
import SocialPage from './pages/admin/SocialPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import AuditPage from './pages/admin/AuditPage';
import RRSManagementPage from './pages/admin/RRSManagementPage';
import KVKManagementPage from './pages/admin/KVKManagementPage';
import ABICManagementPage from './pages/admin/ABICManagementPage';
import AdminPublicationsPage from './pages/admin/PublicationsPage';
import StaffManagementPage from './pages/admin/StaffManagementPage';
import ContactManagementPage from './pages/admin/ContactManagementPage';
import ServicesManagementPage from './pages/admin/ServicesManagementPage';
import ITMUManagementPage from './pages/admin/ITMUManagementPage';
import CadreStrengthManagement from './pages/admin/CadreStrengthManagement';
import CitizenCharterManagement from './pages/admin/CitizenCharterManagement';
import ResearchManagement from './pages/admin/ResearchManagement';
import VKSAManagement from './pages/admin/VKSAManagement';
import AboutManagement from './pages/admin/AboutManagement';
import DivisionsManagementPage from './pages/admin/DivisionsManagementPage';
import PhotosManagementPage from './pages/admin/PhotosManagementPage';
import VideosManagementPage from './pages/admin/VideosManagementPage';
import ForFarmersManagementPage from './pages/admin/ForFarmersManagementPage';
import ProtectedRoute from './components/admin/ProtectedRoute';

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0E6D2B] border-t-transparent"></div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <Router>
          <div className="overflow-x-hidden w-full">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes - Core */}
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/varieties" element={<VarietiesPagePublic />} />
                <Route path="/technologies" element={<TechnologiesPagePublic />} />
                <Route path="/research" element={<ResearchPagePublic />} />
                <Route path="/publications" element={<PublicationsPage />} />
                <Route path="/farmers" element={<FarmersPage />} />
                <Route path="/nursery" element={<NurseryPage />} />
                <Route path="/media" element={<MediaPage />} />
                
                {/* Public Routes - About/Organization */}
                <Route path="/staff" element={<StaffPage />} />
                <Route path="/division" element={<DivisionPage />} />
                <Route path="/cadre-strength" element={<CadreStrengthPage />} />
                <Route path="/vision2050" element={<VisionPage />} />
                <Route path="/citizen-charter" element={<CitizenCharterPage />} />
                <Route path="/collaboration" element={<CollaborationPage />} />
                <Route path="/success-stories" element={<SuccessStoriesPage />} />
                <Route path="/success-stories/:slug" element={<SuccessStoryDetailPage />} />
                <Route path="/education" element={<EducationPage />} />
                <Route path="/training-attended" element={<TrainingAttendedPage />} />
                <Route path="/pme" element={<PMEPage />} />
                <Route path="/akmu" element={<AKMUPage />} />
                
                {/* Public Routes - Facilities */}
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/museum" element={<MuseumPage />} />
                <Route path="/auditorium" element={<AuditoriumPage />} />
                <Route path="/guest-house" element={<GuestHousePage />} />
                
                {/* Public Routes - Research */}
                <Route path="/field-gene-bank" element={<FieldGeneBankPage />} />
                <Route path="/climate-resilient" element={<ClimateResilientPage />} />
                <Route path="/nutraceutical" element={<NutraceuticalPage />} />
                <Route path="/research-funded" element={<ResearchFundedPage />} />
                
                {/* Public Routes - Publications */}
                <Route path="/newsletter" element={<NewsletterPage />} />
                <Route path="/annual-report" element={<AnnualReportPage />} />
                <Route path="/e-publications" element={<EPublicationsPage />} />
                <Route path="/cish-publications" element={<CISHPublicationsPage />} />
                <Route path="/udyan-rashmi" element={<UdyanRashmiPage />} />
                
                {/* Public Routes - Jobs & Tenders */}
                <Route path="/jobs" element={<JobsPagePublic />} />
                <Route path="/tenders" element={<TendersPagePublic />} />
                <Route path="/announcements" element={<AnnouncementsPagePublic />} />
                
                {/* Public Routes - Services */}
                <Route path="/itmu-ipr" element={<ITMUPage />} />
                <Route path="/itmu" element={<ITMUPage />} />
                <Route path="/itmu/ipr-protection" element={<IPRProtectionPage />} />
                <Route path="/abi" element={<ABIPage />} />
                <Route path="/consultancy" element={<ConsultancyPage />} />
                <Route path="/commercialization" element={<CommercializationPage />} />
                <Route path="/contract-research" element={<ContractResearchPage />} />
                <Route path="/contract-services" element={<ContractServicesPage />} />
                <Route path="/service-functions" element={<ServiceFunctionsPage />} />
                
                {/* Public Routes - Outreach */}
                <Route path="/agro-advisory" element={<AgroAdvisoryPage />} />
                <Route path="/plant-bookings" element={<PlantBookingsPage />} />
                <Route path="/vksa" element={<VKSAPage />} />
                {/* <Route path="/farmer-portal" element={<FarmerPortalPage />} /> */}
                {/* <Route path="/farmer-technologies" element={<FarmerTechnologiesPage />} /> */}
                
                {/* Public Routes - Media */}
                <Route path="/photos" element={<PhotosPage />} />
                <Route path="/videos" element={<VideosPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/events/:id" element={<EventDetailPage />} />
                
                {/* Public Routes - Stations */}
                <Route path="/kvk-malda" element={<KVKMaldaPage />} />
                <Route path="/rrs-malda" element={<RRSMaldaPage />} />
                
                {/* Redirect old -new URLs to original pages */}
                <Route path="/kvk-malda-new" element={<Navigate to="/kvk-malda" replace />} />
                <Route path="/rrs-malda-new" element={<Navigate to="/rrs-malda" replace />} />
            
                {/* Admin Routes */}
                <Route path="/admin/login" element={<LoginPage />} />
                <Route path="/admin/dashboard" element={
                  <ProtectedRoute><DashboardPage /></ProtectedRoute>
                } />
                <Route path="/admin/approvals" element={
                  <ProtectedRoute requiredRole="admin"><ApprovalQueuePage /></ProtectedRoute>
                } />
                <Route path="/admin/banners" element={
                  <ProtectedRoute requiredSection="Banners"><BannersPage /></ProtectedRoute>
                } />
                <Route path="/admin/announcements" element={
                  <ProtectedRoute requiredSection="Announcements"><AnnouncementsPage /></ProtectedRoute>
                } />
                <Route path="/admin/director" element={
                  <ProtectedRoute requiredSection="Director's Desk"><DirectorPage /></ProtectedRoute>
                } />
                <Route path="/admin/varieties" element={
                  <ProtectedRoute requiredSection="Varieties"><VarietiesPage /></ProtectedRoute>
                } />
                <Route path="/admin/technologies" element={
                  <ProtectedRoute requiredSection="Technologies"><TechnologiesPage /></ProtectedRoute>
                } />
                <Route path="/admin/research" element={
                  <ProtectedRoute requiredSection="Key Research"><ResearchPage /></ProtectedRoute>
                } />
                <Route path="/admin/jobs" element={
                  <ProtectedRoute requiredSection="Jobs"><JobsPage /></ProtectedRoute>
                } />
                <Route path="/admin/tenders" element={
                  <ProtectedRoute requiredSection="Tenders"><TendersPage /></ProtectedRoute>
                } />
                <Route path="/admin/news" element={
                  <ProtectedRoute requiredSection="News & Events"><NewsPage /></ProtectedRoute>
                } />
                <Route path="/admin/links" element={
                  <ProtectedRoute requiredSection="Useful Links"><LinksPage /></ProtectedRoute>
                } />
                <Route path="/admin/social" element={
                  <ProtectedRoute requiredSection="Social Media"><SocialPage /></ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedRoute requiredRole="superadmin"><UserManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/audit" element={
                  <ProtectedRoute requiredRole="superadmin"><AuditPage /></ProtectedRoute>
                } />
                <Route path="/admin/publications" element={
                  <ProtectedRoute requiredSection="Publications"><AdminPublicationsPage /></ProtectedRoute>
                } />
                <Route path="/admin/rrs-management" element={
                  <ProtectedRoute requiredSection="RRS Malda"><RRSManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/kvk-management" element={
                  <ProtectedRoute requiredSection="KVK Malda"><KVKManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/abic-management" element={
                  <ProtectedRoute requiredSection="ABIC"><ABICManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/staff" element={
                  <ProtectedRoute requiredSection="Staff Management"><StaffManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/contact" element={
                  <ProtectedRoute requiredSection="Contact Page"><ContactManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/services" element={
                  <ProtectedRoute requiredSection="Services"><ServicesManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/itmu" element={
                  <ProtectedRoute requiredSection="ITMU / IPR Cell"><ITMUManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/cadre-strength" element={
                  <ProtectedRoute requiredSection="Cadre Strength"><CadreStrengthManagement /></ProtectedRoute>
                } />
                <Route path="/admin/citizen-charter" element={
                  <ProtectedRoute requiredSection="Citizen Charter"><CitizenCharterManagement /></ProtectedRoute>
                } />
                <Route path="/admin/research-management" element={
                  <ProtectedRoute requiredSection="Research CMS"><ResearchManagement /></ProtectedRoute>
                } />
                <Route path="/admin/vksa" element={
                  <ProtectedRoute requiredSection="VKSA"><VKSAManagement /></ProtectedRoute>
                } />
                <Route path="/admin/about-us" element={
                  <ProtectedRoute requiredSection="About Us"><AboutManagement /></ProtectedRoute>
                } />
                <Route path="/admin/divisions" element={
                  <ProtectedRoute requiredSection="Divisions"><DivisionsManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/photos" element={
                  <ProtectedRoute requiredSection="Photos"><PhotosManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/videos" element={
                  <ProtectedRoute requiredSection="Videos"><VideosManagementPage /></ProtectedRoute>
                } />
                <Route path="/admin/for-farmers" element={
                  <ProtectedRoute requiredSection="For Farmers"><ForFarmersManagementPage /></ProtectedRoute>
                } />
                
                {/* Legal Pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-use" element={<TermsOfUsePage />} />
                
                {/* Fallback */}
                <Route path="*" element={<HomePage />} />
              </Routes>
            </Suspense>
            
            {/* Global ChatBot - appears on all pages */}
            <ChatBot />
            
            {/* Global Modals & Banners */}
            <CookieBanner />
            <ExternalLinkWarning />
          </div>
        </Router>
        <Toaster position="top-right" />
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;

